import React, { useState, useEffect } from 'react';
import { API_BASE } from '../lib/api';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LogOut, Check, X, Users, DollarSign, Eye, ArrowLeft, KeyRound, Ban, Unlock, Trash2, TrendingUp, Calendar, Activity, AlertCircle, Heart, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { TCSLogo } from '../components/TCSLogo';
import { NavArrows } from '../components/NavArrows';

const Admin = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('pending-users');
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingReceipt, setViewingReceipt] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [stats, setStats] = useState(null);
  const [approveDialog, setApproveDialog] = useState(null);
  const [approveDueDay, setApproveDueDay] = useState(5);
  const [editDueDayUser, setEditDueDayUser] = useState(null);
  const [editDueDayValue, setEditDueDayValue] = useState(5);
  const [editDueDateValue, setEditDueDateValue] = useState('');
  const [feedbackUser, setFeedbackUser] = useState(null);
  const [feedbackData, setFeedbackData] = useState(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadStats();
  }, [user]);

  useEffect(() => {
    if (user && user.role === 'admin') {
      loadData();
    }
  }, [tab, user]);

  const loadStats = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/stats`);
      setStats(res.data);
    } catch (e) {
      // silent
    }
  };

  const loadData = async () => {
    setLoading(true);
    setLoadError(false);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      if (tab === 'pending-users' || tab === 'all-users') {
        const status = tab === 'pending-users' ? 'pending' : null;
        const url = status 
          ? `${API_BASE}/api/admin/users?status=${status}`
          : `${API_BASE}/api/admin/users`;
        const res = await axios.get(url, { withCredentials: true, signal: controller.signal });
        setUsers(res.data);
      } else if (tab === 'all-payments') {
        const url = `${API_BASE}/api/admin/payments`;
        const res = await axios.get(url, { withCredentials: true, signal: controller.signal });
        setPayments(res.data);
      }
    } catch (error) {
      if (!axios.isCancel(error)) {
        setLoadError(true);
        toast.error('Erro ao carregar dados. Toque em Tentar novamente.');
      }
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  const handleUserAction = async (userId, action, dueDay = 5) => {
    try {
      await axios.post(
        `${API_BASE}/api/admin/users/approve`,
        { user_id: userId, action, due_day: dueDay },
        { withCredentials: true }
      );
      toast.success(action === 'approve' ? 'Atleta aprovado!' : 'Atleta rejeitado');
      setApproveDialog(null);
      loadStats();
      loadData();
    } catch (error) {
      toast.error('Erro ao processar ação');
    }
  };

  const handleSaveDueDay = async () => {
    if (!editDueDayUser) return;
    if (!editDueDateValue) {
      toast.error('Selecione uma data de vencimento válida');
      return;
    }
    try {
      await axios.put(
        `${API_BASE}/api/admin/users/${editDueDayUser._id}/due-day`,
        { due_date: editDueDateValue }
      );
      const formatted = new Date(editDueDateValue + 'T00:00:00').toLocaleDateString('pt-BR');
      toast.success(`Vencimento de ${editDueDayUser.name} atualizado para ${formatted}`);
      setEditDueDayUser(null);
      loadStats();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar vencimento');
    }
  };

  const openMonthlyFeedback = async (u) => {
    setFeedbackUser(u);
    setFeedbackData(null);
    setFeedbackLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE}/api/admin/users/${u._id}/monthly-feedback`
      );
      setFeedbackData(res.data);
    } catch (error) {
      toast.error('Erro ao gerar feedback mensal');
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleMarkPaid = async (u) => {
    const debt = u.debt_months || 0;
    let confirmMsg;
    if (debt > 1) {
      confirmMsg = `Confirmar pagamento de ${u.name}?\n\nIsto irá QUITAR ${debt} mensalidades pendentes (${(u.unpaid_months || []).join(', ')}).`;
    } else {
      confirmMsg = `Confirmar pagamento do mês de ${u.name}?`;
    }
    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await axios.post(
        `${API_BASE}/api/admin/users/${u._id}/mark-paid`
      );
      const months = res.data?.months_confirmed || [];
      toast.success(months.length > 1
        ? `${months.length} mensalidades de ${u.name} confirmadas!`
        : `Pagamento de ${u.name} confirmado!`);
      loadStats();
      loadData();
    } catch (error) {
      toast.error('Erro ao confirmar pagamento');
    }
  };

  const handleMarkUnpaid = async (u) => {
    if (!window.confirm(`Reverter pagamento do mês de ${u.name}? Use apenas em caso de erro.`)) return;
    try {
      await axios.post(
        `${API_BASE}/api/admin/users/${u._id}/mark-unpaid`
      );
      toast.success('Pagamento revertido');
      loadStats();
      loadData();
    } catch (error) {
      toast.error('Erro ao reverter pagamento');
    }
  };

  const handleBlockUser = async (userId, action) => {
    try {
      await axios.post(
        `${API_BASE}/api/admin/users/block`,
        { user_id: userId, action }
      );
      toast.success(action === 'block' ? 'Atleta bloqueado!' : 'Atleta desbloqueado!');
      loadData();
    } catch (error) {
      toast.error('Erro ao processar ação');
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUser || !newPassword) return;
    if (newPassword.length < 6) {
      toast.error('Senha deve ter no mínimo 6 caracteres');
      return;
    }
    try {
      await axios.post(
        `${API_BASE}/api/admin/users/reset-password`,
        { user_id: resetPasswordUser._id, new_password: newPassword }
      );
      toast.success(`Senha resetada! Passe a nova senha para ${resetPasswordUser.name}`);
      setResetPasswordUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error('Erro ao resetar senha');
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`${API_BASE}/api/admin/users/${deleteConfirm._id}`);
      toast.success('Atleta removido com sucesso');
      setDeleteConfirm(null);
      loadData();
    } catch (error) {
      toast.error('Erro ao remover atleta');
    }
  };

  const handlePaymentAction = async (paymentId, action) => {
    try {
      await axios.post(
        `${API_BASE}/api/admin/payments/confirm`,
        { payment_id: paymentId, action },
        { withCredentials: true }
      );
      toast.success(action === 'confirm' ? 'Pagamento confirmado!' : 'Pagamento rejeitado');
      loadData();
    } catch (error) {
      toast.error('Erro ao processar ação');
    }
  };

  const viewReceipt = async (paymentId) => {
    try {
      const res = await axios.get(
        `${API_BASE}/api/admin/payments/${paymentId}/receipt`,
        { withCredentials: true }
      );
      setViewingReceipt(res.data);
    } catch (error) {
      toast.error('Erro ao carregar comprovante');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header className="sticky top-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <NavArrows />
            <TCSLogo size="sm" showTagline={false} />
            <div>
              <h1 className="text-xl md:text-2xl font-black uppercase tracking-tighter text-[#00F0FF]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Painel Admin
              </h1>
              <p className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA]">
                Thyago Costa Silva
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/admin/financial')}
              variant="outline"
              className="bg-transparent border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14]/10 rounded-none hidden md:inline-flex"
              data-testid="admin-financial-button"
            >
              <DollarSign className="h-4 w-4 mr-2" />
              Faturamento
            </Button>
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="bg-transparent border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF]/10 rounded-none hidden md:inline-flex"
              data-testid="admin-back-button"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Dashboard
            </Button>
            <Button
              onClick={() => navigate('/admin/financial')}
              variant="outline"
              size="icon"
              className="bg-transparent border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14]/10 rounded-none md:hidden"
              data-testid="admin-financial-mobile-button"
              aria-label="Faturamento mensal"
              title="Faturamento mensal"
            >
              <DollarSign className="h-4 w-4" />
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="icon"
              className="bg-transparent border-[#FF3366] text-[#FF3366] hover:bg-[#FF3366]/10 rounded-none"
              data-testid="admin-logout-button"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* === STATS DASHBOARD === */}
        {stats && (
          <div className="mb-8 space-y-6" data-testid="admin-stats">
            {/* Top counters */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-[#121212] border border-white/10 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="h-4 w-4 text-[#00F0FF]" />
                  <p className="text-xs uppercase tracking-wider text-[#A1A1AA]">Atletas Ativos</p>
                </div>
                <p className="text-3xl font-black text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="stats-active">
                  {stats.users.active}
                </p>
                <p className="text-[10px] text-[#A1A1AA] mt-1">
                  {stats.users.plan_bronze} Bronze · {stats.users.plan_prata ?? 0} Prata · {stats.users.plan_ouro} Ouro
                </p>
              </div>
              <div className="bg-[#121212] border border-[#FFB800]/30 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <AlertCircle className="h-4 w-4 text-[#FFB800]" />
                  <p className="text-xs uppercase tracking-wider text-[#A1A1AA]">Pendentes</p>
                </div>
                <p className="text-3xl font-black text-[#FFB800]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="stats-pending">
                  {stats.users.pending}
                </p>
                <p className="text-[10px] text-[#A1A1AA] mt-1">Aguardando aprovação</p>
              </div>
              <div className="bg-[#121212] border border-[#39FF14]/30 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="h-4 w-4 text-[#39FF14]" />
                  <p className="text-xs uppercase tracking-wider text-[#A1A1AA]">Recebido / Mês</p>
                </div>
                <p className="text-3xl font-black text-[#39FF14]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="stats-revenue">
                  R$ {stats.revenue.received_this_month.toFixed(2).replace('.', ',')}
                </p>
                <p className="text-[10px] text-[#A1A1AA] mt-1">
                  {stats.revenue.paid_count} de {stats.users.active} pagos
                </p>
              </div>
              <div className="bg-[#121212] border border-[#FF3366]/30 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <AlertCircle className="h-4 w-4 text-[#FF3366]" />
                  <p className="text-xs uppercase tracking-wider text-[#A1A1AA]">A Receber</p>
                </div>
                <p className="text-3xl font-black text-[#FF3366]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="stats-outstanding">
                  R$ {stats.revenue.outstanding.toFixed(2).replace('.', ',')}
                </p>
                <p className="text-[10px] text-[#A1A1AA] mt-1">
                  Esperado: R$ {stats.revenue.expected_this_month.toFixed(2).replace('.', ',')}
                </p>
              </div>
            </div>

            {/* Declared payments awaiting confirmation - HIGH PRIORITY */}
            {stats.subscriptions.declared && stats.subscriptions.declared.length > 0 && (
              <div className="bg-[#00F0FF]/10 border border-[#00F0FF] p-4" data-testid="admin-declared-block">
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#00F0FF] mb-3 flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Pagamentos Notificados — Aguardando Sua Confirmação
                </p>
                <div className="space-y-2">
                  {stats.subscriptions.declared.map((d) => (
                    <div key={d.user_id} className="flex items-center justify-between bg-[#00F0FF]/5 border-l-2 border-[#00F0FF] px-3 py-2 text-sm gap-2">
                      <div className="flex-1 min-w-0">
                        <span className="text-white font-bold">{d.name}</span>
                        <span className="text-[#A1A1AA] text-xs ml-2">· R$ {d.amount.toFixed(2).replace('.', ',')} · plano {d.plan}</span>
                      </div>
                      <Button
                        onClick={() => handleMarkPaid({ _id: d.user_id, name: d.name })}
                        size="sm"
                        className="bg-[#39FF14] hover:bg-[#39FF14]/80 text-black font-bold rounded-none flex-shrink-0"
                        data-testid={`confirm-declared-${d.user_id}`}
                      >
                        <Check className="h-3 w-3 mr-1" />
                        Confirmar
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Subscription alerts */}
            {(stats.subscriptions.warning_1d.length > 0 || stats.subscriptions.due_today.length > 0 || stats.subscriptions.overdue.length > 0) && (
              <div className="bg-[#121212] border border-[#FFB800]/30 p-4">
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#FFB800] mb-3 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Vencimentos para acompanhar
                </p>
                <div className="space-y-2">
                  {stats.subscriptions.due_today.map((u) => (
                    <div key={u._id} className="flex items-center justify-between bg-[#FF3366]/10 border-l-2 border-[#FF3366] px-3 py-2 text-sm">
                      <span className="text-white">
                        <strong>{u.name}</strong> · vence HOJE
                        {u.due_date && <span className="text-[#A1A1AA] ml-2">({new Date(u.due_date + 'T00:00:00').toLocaleDateString('pt-BR')})</span>}
                      </span>
                      <span className="text-[#FF3366] uppercase text-xs font-bold">Plano {u.plan}</span>
                    </div>
                  ))}
                  {stats.subscriptions.warning_1d.map((u) => (
                    <div key={u._id} className="flex items-center justify-between bg-[#FFB800]/10 border-l-2 border-[#FFB800] px-3 py-2 text-sm">
                      <span className="text-white">
                        <strong>{u.name}</strong> · vence em 1 dia
                        {u.due_date && <span className="text-[#A1A1AA] ml-2">({new Date(u.due_date + 'T00:00:00').toLocaleDateString('pt-BR')})</span>}
                      </span>
                      <span className="text-[#FFB800] uppercase text-xs font-bold">
                        {u.due_date ? new Date(u.due_date + 'T00:00:00').toLocaleDateString('pt-BR') : `Dia ${u.due_day}`}
                      </span>
                    </div>
                  ))}
                  {stats.subscriptions.warning_2d.map((u) => (
                    <div key={u._id} className="flex items-center justify-between bg-[#FFB800]/10 border-l-2 border-[#FFB800]/50 px-3 py-2 text-sm">
                      <span className="text-white">
                        <strong>{u.name}</strong> · vence em 2 dias
                        {u.due_date && <span className="text-[#A1A1AA] ml-2">({new Date(u.due_date + 'T00:00:00').toLocaleDateString('pt-BR')})</span>}
                      </span>
                      <span className="text-[#FFB800] uppercase text-xs font-bold">
                        {u.due_date ? new Date(u.due_date + 'T00:00:00').toLocaleDateString('pt-BR') : `Dia ${u.due_day}`}
                      </span>
                    </div>
                  ))}
                  {stats.subscriptions.overdue.map((u) => (
                    <div key={u._id} className="flex items-center justify-between bg-[#FF3366]/10 border-l-2 border-[#FF3366] px-3 py-2 text-sm">
                      <span className="text-white">
                        <strong>{u.name}</strong>
                        {' · '}
                        {u.debt_months > 1
                          ? `${u.debt_months} mensalidades em aberto`
                          : `vencido há ${Math.abs(u.days_until_due)} ${Math.abs(u.days_until_due) === 1 ? 'dia' : 'dias'}`}
                        {u.due_date && <span className="text-[#A1A1AA] ml-2">(desde {new Date(u.due_date + 'T00:00:00').toLocaleDateString('pt-BR')})</span>}
                      </span>
                      <span className="text-[#FF3366] uppercase text-xs font-bold">Plano {u.plan}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Access flow */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="bg-[#121212] border border-white/10 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Activity className="h-4 w-4 text-[#00F0FF]" />
                  <p className="text-xs uppercase tracking-wider text-[#A1A1AA]">Acessos Hoje</p>
                </div>
                <p className="text-2xl font-black text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {stats.access.today_total}
                </p>
                <p className="text-[10px] text-[#A1A1AA] mt-1">
                  {stats.access.today_unique_users} atletas únicos
                </p>
              </div>
              <div className="bg-[#121212] border border-white/10 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Activity className="h-4 w-4 text-[#A1A1AA]" />
                  <p className="text-xs uppercase tracking-wider text-[#A1A1AA]">Acessos Ontem</p>
                </div>
                <p className="text-2xl font-black text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {stats.access.yesterday_total}
                </p>
                <p className="text-[10px] text-[#A1A1AA] mt-1">
                  {stats.access.yesterday_unique_users} atletas únicos
                </p>
              </div>
              <div className="bg-[#121212] border border-white/10 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="h-4 w-4 text-[#39FF14]" />
                  <p className="text-xs uppercase tracking-wider text-[#A1A1AA]">Acessos no Mês</p>
                </div>
                <p className="text-2xl font-black text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {stats.access.month_total}
                </p>
                <p className="text-[10px] text-[#A1A1AA] mt-1">
                  {stats.access.month_unique_users} atletas únicos
                </p>
              </div>
            </div>
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[#121212] border border-white/10 p-1 rounded-none h-auto mb-6 grid grid-cols-3 gap-1">
            <TabsTrigger value="pending-users" className="data-[state=active]:bg-[#00F0FF] data-[state=active]:text-black rounded-none py-3" data-testid="tab-pending-users">
              <Users className="h-4 w-4 mr-2" />
              Cadastros Pendentes
            </TabsTrigger>
            <TabsTrigger value="all-users" className="data-[state=active]:bg-[#00F0FF] data-[state=active]:text-black rounded-none py-3" data-testid="tab-all-users">
              <Users className="h-4 w-4 mr-2" />
              Todos Atletas
            </TabsTrigger>
            <TabsTrigger value="all-payments" className="data-[state=active]:bg-[#00F0FF] data-[state=active]:text-black rounded-none py-3" data-testid="tab-all-payments">
              <DollarSign className="h-4 w-4 mr-2" />
              Todos Pagamentos
            </TabsTrigger>
          </TabsList>

          {/* Users Tabs */}
          {(tab === 'pending-users' || tab === 'all-users') && (
            <TabsContent value={tab} className="mt-0">
              <div className="bg-[#121212] border border-white/10 p-6">
                {loading ? (
                  <p className="text-center text-[#A1A1AA] py-8">Carregando...</p>
                ) : loadError ? (
                  <div className="text-center py-8">
                    <p className="text-[#FF3366] mb-3">Falha ao carregar a lista. Verifique sua conexão.</p>
                    <Button onClick={loadData} className="bg-[#00F0FF] hover:bg-[#4DFFFF] text-black font-bold rounded-none" data-testid="retry-load-users">
                      Tentar novamente
                    </Button>
                  </div>
                ) : users.length === 0 ? (
                  <p className="text-center text-[#A1A1AA] py-8">Nenhum usuário encontrado</p>
                ) : (
                  <div className="space-y-3">
                    {users.map((u) => (
                      <div key={u._id} className="bg-[#0A0A0A] border border-white/5 p-4 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid={`user-${u._id}`}>
                        <div className="flex-1">
                          {u.low_engagement && (
                            <div className="mb-2 inline-flex items-center gap-2 bg-[#FF3366]/15 border-l-2 border-[#FF3366] px-2 py-1" data-testid={`low-engagement-${u._id}`}>
                              <span className="text-[#FF3366] text-lg leading-none">⚠</span>
                              <span className="text-[#FF3366] text-xs uppercase font-bold tracking-wider">
                                Pouco acesso · {u.weekly_access_days} {u.weekly_access_days === 1 ? 'dia' : 'dias'} nos últimos 7 dias
                              </span>
                            </div>
                          )}
                          <p className="text-white font-semibold">{u.name}</p>
                          <p className="text-sm text-[#A1A1AA]">{u.email}</p>
                          <div className="flex gap-2 mt-2 flex-wrap items-center">
                            <span className={`text-xs px-2 py-0.5 border uppercase font-bold ${
                              u.plan === 'ouro'
                                ? 'bg-[#FFB800]/10 border-[#FFB800] text-[#FFB800]'
                                : u.plan === 'prata'
                                ? 'bg-[#C0C7D1]/10 border-[#C0C7D1] text-[#C0C7D1]'
                                : 'bg-[#00F0FF]/10 border-[#00F0FF] text-[#00F0FF]'
                            }`}>
                              Plano {u.plan}
                            </span>
                            <span className={`text-xs px-2 py-0.5 border uppercase font-bold ${
                              u.status === 'approved' ? 'bg-[#39FF14]/10 border-[#39FF14] text-[#39FF14]' :
                              u.status === 'rejected' ? 'bg-[#FF3366]/10 border-[#FF3366] text-[#FF3366]' :
                              u.status === 'blocked' ? 'bg-[#FF3366]/10 border-[#FF3366] text-[#FF3366]' :
                              'bg-[#FFB800]/10 border-[#FFB800] text-[#FFB800]'
                            }`}>
                              {u.status === 'approved' ? 'Aprovado' : u.status === 'rejected' ? 'Rejeitado' : u.status === 'blocked' ? 'Bloqueado' : 'Pendente'}
                            </span>
                            {u.role === 'admin' && (
                              <span className="text-xs px-2 py-0.5 border border-white text-white uppercase font-bold">Admin</span>
                            )}
                            {u.status === 'approved' && u.role !== 'admin' && (
                              <>
                                <span className="text-xs px-2 py-0.5 border border-white/20 text-[#A1A1AA] uppercase font-bold flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {u.next_due_date
                                    ? `Vence ${new Date(u.next_due_date + 'T00:00:00').toLocaleDateString('pt-BR')}`
                                    : `Vence dia ${u.due_day || 5}`}
                                </span>
                                {u.subscription_status === 'paid' && (
                                  <span className="text-xs px-2 py-0.5 bg-[#39FF14]/10 border border-[#39FF14] text-[#39FF14] uppercase font-bold">PAGO</span>
                                )}
                                {u.payment_declared && u.subscription_status !== 'paid' && (
                                  <span className="text-xs px-2 py-0.5 bg-[#00F0FF]/10 border border-[#00F0FF] text-[#00F0FF] uppercase font-bold animate-pulse" data-testid={`declared-badge-${u._id}`}>
                                    Notificou Pagamento
                                  </span>
                                )}
                                {u.subscription_status === 'overdue' && (
                                  <span className="text-xs px-2 py-0.5 bg-[#FF3366]/10 border border-[#FF3366] text-[#FF3366] uppercase font-bold">
                                    Vencido {Math.abs(u.days_until_due)}d
                                  </span>
                                )}
                                {u.debt_months > 1 && (
                                  <span className="text-xs px-2 py-0.5 bg-[#FF3366]/20 border border-[#FF3366] text-[#FF3366] uppercase font-bold" data-testid={`debt-badge-${u._id}`}>
                                    Dívida: {u.debt_months} meses
                                  </span>
                                )}
                                {u.subscription_status === 'due_today' && (
                                  <span className="text-xs px-2 py-0.5 bg-[#FF3366]/10 border border-[#FF3366] text-[#FF3366] uppercase font-bold">VENCE HOJE</span>
                                )}
                                {u.subscription_status === 'warning_1d' && (
                                  <span className="text-xs px-2 py-0.5 bg-[#FFB800]/10 border border-[#FFB800] text-[#FFB800] uppercase font-bold">Vence amanhã</span>
                                )}
                                {u.subscription_status === 'warning_2d' && (
                                  <span className="text-xs px-2 py-0.5 bg-[#FFB800]/10 border border-[#FFB800] text-[#FFB800] uppercase font-bold">Vence em 2d</span>
                                )}
                              </>
                            )}
                            {u.login_count > 0 && (
                              <span className="text-[10px] text-[#A1A1AA]">
                                {u.login_count} acesso{u.login_count !== 1 ? 's' : ''}
                                {u.last_login && ` · último ${new Date(u.last_login).toLocaleDateString('pt-BR')}`}
                              </span>
                            )}
                          </div>
                        </div>
                        {u.status === 'pending' && (
                          <div className="flex gap-2">
                            <Button
                              onClick={() => { setApproveDialog(u); setApproveDueDay(5); }}
                              size="sm"
                              className="bg-[#39FF14] hover:bg-[#39FF14]/80 text-black font-bold rounded-none"
                              data-testid={`approve-user-${u._id}`}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => handleUserAction(u._id, 'reject')}
                              size="sm"
                              className="bg-[#FF3366] hover:bg-[#FF3366]/80 text-white font-bold rounded-none"
                              data-testid={`reject-user-${u._id}`}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                        {u.status !== 'pending' && u.role !== 'admin' && (
                          <div className="flex gap-2 flex-wrap">
                            {u.subscription_status === 'paid' ? (
                              <Button
                                onClick={() => handleMarkUnpaid(u)}
                                size="sm"
                                variant="outline"
                                className="bg-transparent border-white/30 text-white hover:bg-white/5 rounded-none"
                                data-testid={`mark-unpaid-${u._id}`}
                                title="Reverter pagamento (em caso de erro)"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button
                                onClick={() => handleMarkPaid(u)}
                                size="sm"
                                className={`${u.payment_declared ? 'bg-[#00F0FF] hover:bg-[#00F0FF]/80' : 'bg-[#39FF14] hover:bg-[#39FF14]/80'} text-black font-bold rounded-none`}
                                data-testid={`mark-paid-${u._id}`}
                                title={u.payment_declared ? 'Confirmar pagamento (atleta notificou)' : 'Confirmar pagamento manualmente'}
                              >
                                <DollarSign className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              onClick={() => openMonthlyFeedback(u)}
                              size="sm"
                              variant="outline"
                              className="bg-transparent border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14]/10 rounded-none"
                              data-testid={`monthly-feedback-${u._id}`}
                              title="Ver feedback mensal do atleta"
                            >
                              <Sparkles className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => {
                                setEditDueDayUser(u);
                                setEditDueDayValue(u.due_day || 5);
                                // Pre-fill with the athlete's current next_due_date if any,
                                // otherwise use today's ISO date.
                                const today = new Date();
                                const iso = u.next_due_date || today.toISOString().slice(0, 10);
                                setEditDueDateValue(iso);
                              }}
                              size="sm"
                              variant="outline"
                              className="bg-transparent border-[#FFB800] text-[#FFB800] hover:bg-[#FFB800]/10 rounded-none"
                              data-testid={`edit-due-day-${u._id}`}
                              title="Editar data de vencimento"
                            >
                              <Calendar className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => setResetPasswordUser(u)}
                              size="sm"
                              variant="outline"
                              className="bg-transparent border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF]/10 rounded-none"
                              data-testid={`reset-password-${u._id}`}
                              title="Resetar senha"
                            >
                              <KeyRound className="h-4 w-4" />
                            </Button>
                            {u.status === 'blocked' ? (
                              <Button
                                onClick={() => handleBlockUser(u._id, 'unblock')}
                                size="sm"
                                className="bg-[#39FF14] hover:bg-[#39FF14]/80 text-black font-bold rounded-none"
                                data-testid={`unblock-user-${u._id}`}
                                title="Desbloquear"
                              >
                                <Unlock className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button
                                onClick={() => handleBlockUser(u._id, 'block')}
                                size="sm"
                                variant="outline"
                                className="bg-transparent border-[#FFB800] text-[#FFB800] hover:bg-[#FFB800]/10 rounded-none"
                                data-testid={`block-user-${u._id}`}
                                title="Bloquear acesso"
                              >
                                <Ban className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              onClick={() => setDeleteConfirm(u)}
                              size="sm"
                              variant="outline"
                              className="bg-transparent border-[#FF3366] text-[#FF3366] hover:bg-[#FF3366]/10 rounded-none"
                              data-testid={`delete-user-${u._id}`}
                              title="Remover atleta"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          )}

          {/* Payments Tabs */}
          {(tab === 'all-payments') && (
            <TabsContent value={tab} className="mt-0">
              <div className="bg-[#121212] border border-white/10 p-6">
                {loading ? (
                  <p className="text-center text-[#A1A1AA] py-8">Carregando...</p>
                ) : loadError ? (
                  <div className="text-center py-8">
                    <p className="text-[#FF3366] mb-3">Falha ao carregar pagamentos. Verifique sua conexão.</p>
                    <Button onClick={loadData} className="bg-[#00F0FF] hover:bg-[#4DFFFF] text-black font-bold rounded-none" data-testid="retry-load-payments">
                      Tentar novamente
                    </Button>
                  </div>
                ) : payments.length === 0 ? (
                  <p className="text-center text-[#A1A1AA] py-8">Nenhum pagamento encontrado</p>
                ) : (
                  <div className="space-y-3">
                    {payments.map((p) => (
                      <div key={p._id} className={`bg-[#0A0A0A] border ${p.is_virtual ? 'border-[#FF3366]/40' : 'border-white/5'} p-4`} data-testid={`payment-${p._id}`}>
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                          <div className="flex-1 min-w-[200px]">
                            <p className="text-white font-semibold">{p.user_name}</p>
                            <p className="text-sm text-[#A1A1AA]">{p.user_email}</p>
                            <p className="text-sm text-white mt-1">
                              <strong>R$ {p.amount?.toFixed(2).replace('.', ',')}</strong> · {p.month} · Plano {p.plan}
                            </p>
                            {p.due_date && (
                              <p className="text-xs text-[#FF3366] mt-0.5 font-bold">
                                Vencimento: {new Date(p.due_date + 'T00:00:00').toLocaleDateString('pt-BR')}
                              </p>
                            )}
                            {p.submitted_at ? (
                              <p className="text-xs text-[#A1A1AA]">
                                Enviado em {new Date(p.submitted_at).toLocaleDateString('pt-BR')}
                              </p>
                            ) : p.is_virtual ? (
                              <p className="text-xs text-[#FF3366] font-bold mt-0.5">
                                ⚠ Atleta não declarou pagamento
                              </p>
                            ) : null}
                            {p.notes && <p className="text-xs text-white mt-1">Obs: {p.notes}</p>}
                          </div>
                          <div className="flex gap-2 items-center">
                            <span className={`text-xs px-2 py-1 border uppercase font-bold ${
                              p.status === 'confirmed' ? 'bg-[#39FF14]/10 border-[#39FF14] text-[#39FF14]' :
                              p.status === 'rejected' ? 'bg-[#FF3366]/10 border-[#FF3366] text-[#FF3366]' :
                              p.status === 'owed' ? 'bg-[#FF3366]/20 border-[#FF3366] text-[#FF3366]' :
                              p.status === 'declared' ? 'bg-[#00F0FF]/10 border-[#00F0FF] text-[#00F0FF]' :
                              'bg-[#FFB800]/10 border-[#FFB800] text-[#FFB800]'
                            }`}>
                              {p.status === 'confirmed' ? 'Confirmado'
                                : p.status === 'rejected' ? 'Rejeitado'
                                : p.status === 'owed' ? 'Em débito'
                                : p.status === 'declared' ? 'Notificado'
                                : 'Pendente'}
                            </span>
                            {!p.is_virtual && (
                              <Button
                                onClick={() => viewReceipt(p._id)}
                                size="sm"
                                variant="outline"
                                className="bg-transparent border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF]/10 rounded-none"
                                data-testid={`view-receipt-${p._id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            )}
                            {(p.status === 'pending' || p.status === 'declared' || p.status === 'owed') && (
                              <Button
                                onClick={() => handleMarkPaid({ _id: p.user_id, name: p.user_name, debt_months: 0 })}
                                size="sm"
                                className="bg-[#39FF14] hover:bg-[#39FF14]/80 text-black font-bold rounded-none"
                                data-testid={`confirm-owed-${p._id}`}
                                title="Confirmar pagamento (quita tudo em aberto)"
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                            )}
                            {p.status === 'pending' && !p.is_virtual && (
                              <>
                                <Button
                                  onClick={() => handlePaymentAction(p._id, 'confirm')}
                                  size="sm"
                                  className="bg-[#39FF14] hover:bg-[#39FF14]/80 text-black font-bold rounded-none hidden"
                                  data-testid={`confirm-payment-${p._id}`}
                                >
                                  <Check className="h-4 w-4" />
                                </Button>
                                <Button
                                  onClick={() => handlePaymentAction(p._id, 'reject')}
                                  size="sm"
                                  className="bg-[#FF3366] hover:bg-[#FF3366]/80 text-white font-bold rounded-none"
                                  data-testid={`reject-payment-${p._id}`}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </main>

      {/* Approve User Dialog (with due_day picker) */}
      <Dialog open={!!approveDialog} onOpenChange={() => setApproveDialog(null)}>
        <DialogContent className="bg-[#121212] border-white/10 text-white rounded-none">
          <DialogHeader>
            <DialogTitle className="uppercase font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Aprovar Atleta
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              <strong className="text-white">{approveDialog?.name}</strong> ({approveDialog?.email}) — Plano {approveDialog?.plan}.
              Selecione o dia de vencimento da mensalidade deste atleta.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-2 block">Dia do Vencimento (1 a 31)</Label>
              <Input
                type="number"
                min={1}
                max={31}
                value={approveDueDay}
                onChange={(e) => setApproveDueDay(parseInt(e.target.value, 10) || 1)}
                className="bg-[#0A0A0A] border-white/10 text-white focus:border-[#00F0FF] rounded-none h-11"
                data-testid="approve-due-day-input"
              />
              <p className="text-xs text-[#A1A1AA] mt-1">A mensalidade vencerá todo dia {approveDueDay} de cada mês.</p>
            </div>
            <Button
              onClick={() => handleUserAction(approveDialog._id, 'approve', approveDueDay)}
              className="w-full bg-[#39FF14] hover:bg-[#39FF14]/80 text-black font-bold uppercase rounded-none h-12"
              data-testid="approve-confirm-button"
            >
              <Check className="h-4 w-4 mr-2" />
              Aprovar com vencimento dia {approveDueDay}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Due Date Dialog */}
      <Dialog open={!!editDueDayUser} onOpenChange={() => setEditDueDayUser(null)}>
        <DialogContent className="bg-[#121212] border-white/10 text-white rounded-none">
          <DialogHeader>
            <DialogTitle className="uppercase font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Editar Data de Vencimento
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Atualizando vencimento de <strong className="text-white">{editDueDayUser?.name}</strong>.
              {editDueDayUser?.next_due_date && (
                <> Atual: <strong className="text-[#FFB800]">{new Date(editDueDayUser.next_due_date + 'T00:00:00').toLocaleDateString('pt-BR')}</strong>.</>
              )}
              <br />
              <span className="text-[10px]">Selecione <strong>dia, mês e ano</strong> do próximo vencimento. Use para planos <strong>mensais</strong>, <strong>trimestrais</strong> ou <strong>anuais</strong>.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-2 block">Nova Data de Vencimento</Label>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <p className="text-[10px] tracking-widest text-[#A1A1AA] uppercase mb-1">Dia</p>
                  <select
                    value={editDueDateValue ? parseInt(editDueDateValue.slice(8, 10), 10) : ''}
                    onChange={(e) => {
                      const day = String(e.target.value).padStart(2, '0');
                      const [y, m] = (editDueDateValue || new Date().toISOString().slice(0, 10)).split('-');
                      setEditDueDateValue(`${y}-${m}-${day}`);
                    }}
                    className="w-full bg-[#0A0A0A] border border-white/10 text-white focus:border-[#FFB800] rounded-none h-11 px-2"
                    data-testid="edit-due-date-day"
                  >
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                      <option key={d} value={d}>{String(d).padStart(2, '0')}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <p className="text-[10px] tracking-widest text-[#A1A1AA] uppercase mb-1">Mês</p>
                  <select
                    value={editDueDateValue ? parseInt(editDueDateValue.slice(5, 7), 10) : ''}
                    onChange={(e) => {
                      const month = String(e.target.value).padStart(2, '0');
                      const [y, , d] = (editDueDateValue || new Date().toISOString().slice(0, 10)).split('-');
                      setEditDueDateValue(`${y}-${month}-${d}`);
                    }}
                    className="w-full bg-[#0A0A0A] border border-white/10 text-white focus:border-[#FFB800] rounded-none h-11 px-2"
                    data-testid="edit-due-date-month"
                  >
                    {['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'].map((label, idx) => (
                      <option key={idx + 1} value={idx + 1}>{String(idx + 1).padStart(2, '0')} — {label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <p className="text-[10px] tracking-widest text-[#A1A1AA] uppercase mb-1">Ano</p>
                  <select
                    value={editDueDateValue ? parseInt(editDueDateValue.slice(0, 4), 10) : ''}
                    onChange={(e) => {
                      const year = e.target.value;
                      const [, m, d] = (editDueDateValue || new Date().toISOString().slice(0, 10)).split('-');
                      setEditDueDateValue(`${year}-${m}-${d}`);
                    }}
                    className="w-full bg-[#0A0A0A] border border-white/10 text-white focus:border-[#FFB800] rounded-none h-11 px-2"
                    data-testid="edit-due-date-year"
                  >
                    {(() => {
                      const cy = new Date().getFullYear();
                      return Array.from({ length: 8 }, (_, i) => cy + i);
                    })().map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
              <p className="text-xs text-[#A1A1AA] mt-3">
                <strong className="text-white">Atalhos:</strong>{' '}
                <button
                  type="button"
                  className="underline text-[#00F0FF] hover:text-[#4DFFFF]"
                  onClick={() => {
                    const d = new Date(); d.setMonth(d.getMonth() + 1);
                    setEditDueDateValue(d.toISOString().slice(0, 10));
                  }}
                  data-testid="preset-monthly"
                >+1 mês</button>{' · '}
                <button
                  type="button"
                  className="underline text-[#00F0FF] hover:text-[#4DFFFF]"
                  onClick={() => {
                    const d = new Date(); d.setMonth(d.getMonth() + 3);
                    setEditDueDateValue(d.toISOString().slice(0, 10));
                  }}
                  data-testid="preset-trimestral"
                >+3 meses (trimestral)</button>{' · '}
                <button
                  type="button"
                  className="underline text-[#00F0FF] hover:text-[#4DFFFF]"
                  onClick={() => {
                    const d = new Date(); d.setFullYear(d.getFullYear() + 1);
                    setEditDueDateValue(d.toISOString().slice(0, 10));
                  }}
                  data-testid="preset-anual"
                >+1 ano (anual)</button>
              </p>
            </div>
            <Button
              onClick={handleSaveDueDay}
              disabled={!editDueDateValue}
              className="w-full bg-[#FFB800] hover:bg-[#FFB800]/80 text-black font-bold uppercase rounded-none h-12"
              data-testid="save-due-day-button"
            >
              Salvar Vencimento {editDueDateValue ? new Date(editDueDateValue + 'T00:00:00').toLocaleDateString('pt-BR') : ''}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Monthly Feedback Dialog */}
      <Dialog open={!!feedbackUser} onOpenChange={() => { setFeedbackUser(null); setFeedbackData(null); }}>
        <DialogContent className="bg-[#121212] border-white/10 text-white rounded-none max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="uppercase font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <Sparkles className="h-5 w-5 text-[#39FF14]" />
              Feedback Mensal — {feedbackUser?.name}
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Análise consolidada dos últimos 30 dias com base no questionário diário e CTL.
            </DialogDescription>
          </DialogHeader>
          {feedbackLoading && <p className="text-[#A1A1AA] py-6 text-center">Gerando análise…</p>}
          {feedbackData && (
            <div className="space-y-4 mt-4" data-testid="monthly-feedback-content">
              {/* Summary */}
              <div className="bg-[#00F0FF]/5 border border-[#00F0FF]/30 p-4">
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#00F0FF] mb-2">Resumo Executivo</p>
                <p className="text-sm md:text-base text-white leading-relaxed">{feedbackData.summary}</p>
              </div>

              {/* Highlights */}
              {feedbackData.highlights && feedbackData.highlights.length > 0 && (
                <div className="bg-[#39FF14]/5 border border-[#39FF14]/30 p-4">
                  <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#39FF14] mb-3 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" /> Benefícios do Protocolo TCS
                  </p>
                  <p className="text-[11px] text-[#A1A1AA] mb-3">Ganhos atribuídos ao acompanhamento contínuo do atleta no protocolo:</p>
                  <ul className="space-y-2">
                    {feedbackData.highlights.map((h, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-white">
                        <Check className="h-4 w-4 text-[#39FF14] flex-shrink-0 mt-0.5" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Improvements */}
              {feedbackData.improvements && feedbackData.improvements.length > 0 && (
                <div className="bg-[#FFB800]/5 border border-[#FFB800]/30 p-4">
                  <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#FFB800] mb-3 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" /> Pontos a Melhorar
                  </p>
                  <ul className="space-y-2">
                    {feedbackData.improvements.map((m, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-white">
                        <ChevronUp className="h-4 w-4 text-[#FFB800] flex-shrink-0 mt-0.5" />
                        <span>{m}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Metrics */}
              {feedbackData.has_data && (
                <div className="bg-[#0A0A0A] border border-white/10 p-4">
                  <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-3">Métricas do Ciclo</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    <div><span className="text-[#A1A1AA]">Dias registrados:</span> <strong className="text-white">{feedbackData.metrics.days_tracked}</strong></div>
                    <div><span className="text-[#A1A1AA]">Score médio:</span> <strong className="text-white">{feedbackData.metrics.avg_score}/100</strong></div>
                    <div><span className="text-[#A1A1AA]">Sensação:</span> <strong className="text-white">{feedbackData.metrics.avg_feeling}/10</strong></div>
                    <div><span className="text-[#A1A1AA]">Dores:</span> <strong className="text-white">{feedbackData.metrics.avg_soreness}/10</strong></div>
                    <div><span className="text-[#A1A1AA]">Recuperação:</span> <strong className="text-white">{feedbackData.metrics.avg_recovery}/10</strong></div>
                    <div><span className="text-[#A1A1AA]">Mental:</span> <strong className="text-white">{feedbackData.metrics.avg_mental}/10</strong></div>
                    <div><span className="text-[#A1A1AA]">FC baixa:</span> <strong className="text-[#39FF14]">{feedbackData.metrics.fc_baixa_percent}%</strong></div>
                    <div><span className="text-[#A1A1AA]">FC alta:</span> <strong className="text-[#FF3366]">{feedbackData.metrics.fc_alta_percent}%</strong></div>
                    {feedbackData.metrics.ctl_current !== null && feedbackData.metrics.ctl_current !== undefined && (
                      <div><span className="text-[#A1A1AA]">CTL atual:</span> <strong className="text-[#00F0FF]">{feedbackData.metrics.ctl_current}</strong>{feedbackData.metrics.ctl_delta !== null && ` (${feedbackData.metrics.ctl_delta >= 0 ? '+' : ''}${feedbackData.metrics.ctl_delta})`}</div>
                    )}
                    {feedbackData.metrics.atl_current !== null && feedbackData.metrics.atl_current !== undefined && (
                      <div data-testid="metric-atl"><span className="text-[#A1A1AA]">Fadiga (ATL):</span> <strong className="text-[#FFB800]">{feedbackData.metrics.atl_current}</strong>{feedbackData.metrics.atl_delta !== null && feedbackData.metrics.atl_delta !== undefined && ` (${feedbackData.metrics.atl_delta >= 0 ? '+' : ''}${feedbackData.metrics.atl_delta})`}</div>
                    )}
                    {feedbackData.metrics.tsb_current !== null && feedbackData.metrics.tsb_current !== undefined && (
                      <div data-testid="metric-tsb"><span className="text-[#A1A1AA]">Forma (TSB):</span> <strong className={feedbackData.metrics.tsb_current >= 0 ? 'text-[#39FF14]' : 'text-[#FF3366]'}>{feedbackData.metrics.tsb_current >= 0 ? '+' : ''}{feedbackData.metrics.tsb_current}</strong>{feedbackData.metrics.tsb_delta !== null && feedbackData.metrics.tsb_delta !== undefined && ` (${feedbackData.metrics.tsb_delta >= 0 ? '+' : ''}${feedbackData.metrics.tsb_delta})`}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPasswordUser} onOpenChange={() => { setResetPasswordUser(null); setNewPassword(''); }}>
        <DialogContent className="bg-[#121212] border-white/10 text-white rounded-none">
          <DialogHeader>
            <DialogTitle className="uppercase font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Resetar Senha
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Defina uma nova senha para <strong className="text-white">{resetPasswordUser?.name}</strong> ({resetPasswordUser?.email}). Depois passe essa senha ao atleta para ele fazer login. <span className="text-[#00F0FF]">Isto também limpa qualquer bloqueio por tentativas.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-2 block">Nova Senha</Label>
              <Input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Digite a nova senha (mín. 6 caracteres)"
                className="bg-[#0A0A0A] border-white/10 text-white focus:border-[#00F0FF] rounded-none h-11"
                data-testid="admin-new-password-input"
              />
            </div>
            <Button
              onClick={handleResetPassword}
              disabled={!newPassword || newPassword.length < 6}
              className="w-full bg-[#00F0FF] hover:bg-[#4DFFFF] text-black font-bold uppercase rounded-none h-12"
              data-testid="admin-reset-password-submit"
            >
              Resetar Senha
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-[#121212] border-[#FF3366]/40 text-white rounded-none">
          <DialogHeader>
            <DialogTitle className="uppercase font-bold tracking-tight text-[#FF3366]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Remover Atleta
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Tem certeza que deseja remover <strong className="text-white">{deleteConfirm?.name}</strong>?
              <br /><br />
              <span className="text-[#FF3366]">⚠ Esta ação é irreversível!</span> Todos os dados do atleta (wellness, CTL, pagamentos) serão apagados permanentemente.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button
              onClick={() => setDeleteConfirm(null)}
              variant="outline"
              className="flex-1 bg-transparent border-white/20 text-white hover:bg-white/5 rounded-none h-12"
              data-testid="admin-cancel-delete"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteUser}
              className="flex-1 bg-[#FF3366] hover:bg-[#FF3366]/80 text-white font-bold uppercase rounded-none h-12"
              data-testid="admin-confirm-delete"
            >
              Remover
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt Viewer Dialog */}
      <Dialog open={!!viewingReceipt} onOpenChange={() => setViewingReceipt(null)}>
        <DialogContent className="bg-[#121212] border-white/10 text-white rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="uppercase font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Comprovante: {viewingReceipt?.file_name}
            </DialogTitle>
          </DialogHeader>
          {viewingReceipt?.file_type?.startsWith('image/') ? (
            <img 
              src={`data:${viewingReceipt.file_type};base64,${viewingReceipt.file_data}`} 
              alt="Comprovante"
              className="w-full h-auto"
            />
          ) : (
            <a 
              href={`data:${viewingReceipt?.file_type};base64,${viewingReceipt?.file_data}`}
              download={viewingReceipt?.file_name}
              className="text-[#00F0FF] underline"
            >
              Baixar {viewingReceipt?.file_name}
            </a>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
